import { Trans, useLingui } from '@lingui/react/macro';

import Message from '@/design_system/Message';
import PhotoCard from '@/design_system/PhotoCard';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import {
  getArticlePhotoOrConditionPhoto,
  getProductPictureFromArticleColor,
} from '@/models/article';
import { getExternalIdNameCollection, useColorOptions } from '@/models/product';
import { useSizesOptionsSorted } from '@/models/sizeProduct';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useDatabaseTranslation } from '@/services/i18n';
import { formatCurrency, formatCurrencyRange, formatWeight } from '@/utils/number';
import useViewPort from '@/utils/useViewport';

import { useProductAndUniqueArticleInputs } from '../../useProductAndUniqueArticleInputs';

import { Item } from './Item';

export const ArticleDetails = () => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const { isMobile } = useViewPort();

  const { article, errors, view, productAttributes } = useArticleContext();
  const productAndUniqueArticleInputs = useProductAndUniqueArticleInputs();

  const { sizesOptions } = useSizesOptionsSorted({ article });

  const productName = article.product ? getExternalIdNameCollection(article.product) : '-';

  const colorOptions = useColorOptions({ article });
  const selectedProductAttributeValueIds = article.productAttributeValues.map((value) => value.id);
  const articlePhoto = getArticlePhotoOrConditionPhoto(article);
  const visibleProductAttributes =
    productAttributes?.filter((productAttribute) => {
      const hasSelectedValue = article.productAttributeValues.some((productAttributeValue) =>
        productAttribute.productAttributeValues.some(
          (value) => value.id === productAttributeValue.id
        )
      );

      return (
        hasSelectedValue ||
        !productAttribute.parentProductAttributeValueId ||
        selectedProductAttributeValueIds.includes(productAttribute.parentProductAttributeValueId)
      );
    }) ?? [];

  const productPicture = getProductPictureFromArticleColor({ article });

  return (
    <Stack gap="0.75rem">
      <Stack row gap="1rem" flexWrap="nowrap">
        {!isMobile && (!!articlePhoto || !productPicture) && (
          <PhotoCard
            url={articlePhoto?.url}
            name={t({ id: 'article.information.article-photo', message: 'Item photo' })}
            alt={t({ id: 'article.information.article-photo', message: 'Item photo' })}
            size="large"
          />
        )}

        {!!productPicture && (
          <div>
            {isMobile && (
              <InlineText size="200" color="secondary">
                <Trans id="article.information.product-photo">Product photo</Trans>
              </InlineText>
            )}
            <PhotoCard
              url={productPicture}
              name={productPicture}
              alt={productPicture}
              size={isMobile ? 'xxlarge' : 'large'}
            />
          </div>
        )}
      </Stack>

      {errors.details?.missingArticlePhoto && (
        <Message type="error">
          <Trans id="article.information.article-photo.error">Please add a photo of the item</Trans>
        </Message>
      )}

      {errors.details?.missingArticlePhotoForRefashion && (
        <Message type="error">
          <Trans id="requests.new.articles.alert.missing-article-photo-for-refashion">
            An article photo is required when an action with repair bonus is added.
          </Trans>
        </Message>
      )}

      {view.details.brand.shown && (
        <Item
          label={t({ id: 'article.information.brand', message: 'Brand' })}
          value={
            article.product?.otherBrandName ??
            article.product?.brand?.name ??
            article.otherBrandName ??
            article.brand?.name
          }
          error={
            errors.details?.otherBrandEmpty
              ? t({
                  id: 'article.form.brand.other.specify-name.error',
                  message: 'Please select a brand name',
                })
              : undefined
          }
        />
      )}

      {view.details.serialNumber.shown && (
        <Item
          label={t({ id: 'article.information.unique-article', message: 'Serial number' })}
          value={article.uniqueArticle?.serialNumber ?? '-'}
          error={
            errors.details?.product && !productAndUniqueArticleInputs.uniqueArticleInput.optional
              ? productAndUniqueArticleInputs.errorMessage
              : undefined
          }
        />
      )}

      <Item
        label={t({ id: 'article.information.article-reference', message: 'Item reference' })}
        value={productName}
        error={
          errors.details?.product && !productAndUniqueArticleInputs.productInput.optional
            ? productAndUniqueArticleInputs.errorMessage
            : undefined
        }
      />

      <Item
        label={t({ id: 'article.information.product-reference', message: 'Product reference' })}
        value={article.product?.data.reference}
      />

      {visibleProductAttributes.map((productAttribute) => {
        const selected = article.productAttributeValues.find((productAttributeValue) =>
          productAttribute.productAttributeValues.some(
            (value) => value.id === productAttributeValue.id
          )
        );
        const isRootProductAttribute = !productAttribute.parentProductAttributeValueId;

        return (
          <Item
            key={productAttribute.id}
            label={_db(productAttribute.name)}
            value={selected ? _db(selected.name) : undefined}
            error={
              isRootProductAttribute && errors.details?.product
                ? productAndUniqueArticleInputs.errorMessage
                : errors.details?.productAttributes?.find(({ id }) => id === productAttribute.id)
                      ?.missingValue
                  ? t({
                      id: 'article.form.product-attribute-value.error',
                      message: 'Please select a value',
                    })
                  : undefined
            }
          />
        );
      })}

      <Item
        label={t({ id: 'article.information.collection', message: 'Collection' })}
        value={article.product?.data.collection}
      />

      {view.details.color.shown && (
        <Item
          label={t({ id: 'article.information.color', message: 'Color' })}
          value={
            colorOptions.length > 0
              ? article.color
                ? _db(article.color)
                : undefined
              : t({ id: '_general.na', message: 'N/A' })
          }
          error={
            errors?.details?.missingColor
              ? t({
                  id: 'article.information.color.error',
                  message: 'Please select a color',
                })
              : undefined
          }
        />
      )}

      {view.details.size.shown && (
        <Item
          label={t({ id: 'article.information.size', message: 'Size' })}
          value={
            sizesOptions.length > 0
              ? article.size
                ? _db(article.size)
                : undefined
              : t({ id: '_general.na', message: 'N/A' })
          }
          error={
            errors?.details?.missingSize
              ? t({
                  id: 'article.information.size.error',
                  message: 'Please select a size',
                })
              : undefined
          }
        />
      )}

      {view.details.weight.shown && (
        <Item
          label={t({ id: 'article.information.weight', message: 'Weight' })}
          value={formatWeight(article.weight)}
          error={
            errors?.details?.missingWeight
              ? t({
                  id: 'article.information.weight.error',
                  message: 'Please select a weight',
                })
              : undefined
          }
        />
      )}

      {article.product?.data.purchaseValue !== undefined && (
        <Item
          label={t({ id: 'article.information.retail-price', message: 'Retail price' })}
          value={formatCurrency(
            article.product.data.purchaseValue.amount,
            article.product.data.purchaseValue.currency
          )}
        />
      )}

      {view.details.estimatedValue.shown && (
        <Item
          label={t({ id: 'article.information.estimated-value', message: 'Estimated value' })}
          value={
            article.data.estimatedValue === 'unknown' ? (
              <Trans id="article.information.estimated-value.unknown">Unknown</Trans>
            ) : (
              formatCurrencyRange(article.data.estimatedValue, {
                maximumFractionDigits: 0,
              })
            )
          }
          error={
            errors?.details?.missingEstimatedValue
              ? t({
                  id: 'article.information.estimated-value.error',
                  message: 'Please select an estimated value',
                })
              : undefined
          }
        />
      )}
    </Stack>
  );
};
